import { ref, computed, type Ref, watch } from "vue";
import { defineStore } from "pinia";
import type { DecodedToken, Token } from "../models/auth";
import * as authApi from "../services/auth";
import type { LoadState } from "../models/state";
import { refreshToken, TokenResponse } from "../services/auth";
import { getActivePinia } from "pinia";
import * as Sentry from "@sentry/browser";

const rawUser = localStorage.getItem("user");
const decodedUser = rawUser ? JSON.parse(rawUser) : null;

import {
  stateInitial,
  stateLoading,
  stateLoaded,
  stateError,
} from "../models/state";
import { head, update } from "lodash";
import { useRoute, useRouter } from "vue-router";
import { decode } from "querystring";
import { publicPages } from "@shared/router/loginGuard";
import axios from "axios";
import { authConfig } from "@xeptum/settings/openidSettings";

export const useAuthStore = defineStore("auth", () => {
  const provider = ref<"KeyCloak" | "eqAccount">("eqAccount");

  const keyCloakConfig = {
    client_id: "smx-portal",
    realmName: "inctec-portal",
  };

  const persistentLocalStorageValues = ref([]);

  /**
   * Use this to keep localStorage values when logging out, all other local storage values will be removed
   **/
  function setPersistentStorageValue(value: string) {
    persistentLocalStorageValues.value.push(value);
  }

  function setRealmName(name: string) {
    keyCloakConfig.realmName = name;
  }

  const tokenState: Ref<Token & { loadingState: LoadState<void> }> =
    decodedUser && decodedUser.access && decodedUser.refresh
      ? ref(decodedUser)
      : ref({
          refresh: null,
          access: null,
          loadingState: stateInitial,
        });

  async function login({
    username,
    password,
    termsAndConditions,
  }: {
    username: string;
    password: string;
    termsAndConditions: boolean;
  }) {
    tokenState.value.loadingState = stateLoading;
    if (provider.value === "KeyCloak") {
      await authApi
        .loginKeyCloak(
          {
            client_id: keyCloakConfig.client_id,
            grant_type: "password",
            password,
            username,
          },
          keyCloakConfig.realmName,
        )
        .then(
          (response) => {
            updateToken({
              access: response.data.access_token,
              refresh: response.data.refresh_token,
            });
            return response;
          },
          (error) => {
            tokenState.value.loadingState = stateError;
            throw error;
          },
        );
    } else {
      await authApi.login(username, password, termsAndConditions).then(
        (response) => {
          updateToken({
            access: response.data.token,
            refresh: response.data.refresh,
          });
          return response;
        },
        (error) => {
          tokenState.value.loadingState = stateError;
          throw error;
        },
      );
    }
  }

  const router = useRouter();

  function logout() {
    const wasLoggedIn =
      tokenState.value.refresh !== null &&
      tokenState.value.refresh !== undefined;

    const isDevEnvironment = process.env.NODE_ENV == "development";
    const baseUrl = isDevEnvironment
      ? "https://smart-dev.inctec.net"
      : window.location.origin;

    if (tokenState.value.access) {
      const revokeAccessFormData = new URLSearchParams();
      revokeAccessFormData.append("client_id", keyCloakConfig.client_id);
      revokeAccessFormData.append("token", tokenState.value.access);
      revokeAccessFormData.append("token_type_hint", "access_token");

      axios
        .post(
          `${baseUrl}/api/auth/realms/${keyCloakConfig.realmName}/protocol/openid-connect/revoke`,
          revokeAccessFormData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        )
        .catch((e) => {
          Sentry.captureException(e);
        });
    }

    if (tokenState.value.refresh) {
      const revokeRefreshFormData = new URLSearchParams();
      revokeRefreshFormData.append("client_id", keyCloakConfig.client_id);
      revokeRefreshFormData.append("token", tokenState.value.refresh);
      revokeRefreshFormData.append("token_type_hint", "refresh_token");

      axios
        .post(
          `${baseUrl}/api/auth/realms/${keyCloakConfig.realmName}/protocol/openid-connect/revoke`,
          revokeRefreshFormData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        )
        .catch((e) => {
          Sentry.captureException(e);
        });
    }

    if (tokenState.value.refresh) {
      // Create form data with required parameters
      const formData = new URLSearchParams();
      // https://www.keycloak.org/securing-apps/oidc-layers
      formData.append("client_id", keyCloakConfig.client_id);
      formData.append("refresh_token", tokenState.value.refresh);
      axios
        .post(
          `${isDevEnvironment ? "https://smart-dev.inctec.net" : window.location.origin}/api/auth/realms/inctec-portal/protocol/openid-connect/logout`,
          formData,
        )
        .catch((e) => {
          Sentry.captureException(e);
        });
    }

    tokenState.value.access = null;
    tokenState.value.refresh = null;

    const persistentValues = persistentLocalStorageValues.value.map((key) => ({
      key,
      value: localStorage.getItem(key),
    }));

    localStorage.clear();

    // Clear all cookies
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    persistentValues.forEach((item) => {
      localStorage.setItem(item.key, item.value);
    });

    const isPublicPage = publicPages.includes(window.location.pathname);
    if (!isPublicPage) {
      void router.push({ name: "login" }).then(() => {
        if (wasLoggedIn) {
          window.location.reload();
        }
      });
    }
  }

  function updateToken(token: Token) {
    tokenState.value = { ...token, loadingState: stateLoaded };
    document.cookie = `token=${token.access};path=/`;
    localStorage.setItem("user", JSON.stringify(tokenState.value));
  }

  const decodedToken = computed<DecodedToken>(() => {
    const token = tokenState.value.access;
    if (token === null) {
      return null;
    }
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  });

  const roles = computed(() => {
    return decodedToken.value?.realm_access.roles;
  });

  async function refreshToken() {
    try {
      const response =
        provider.value === "KeyCloak"
          ? await authApi
              .refreshKeyCloak(
                tokenState.value.refresh,
                keyCloakConfig.client_id,
                keyCloakConfig.realmName,
              )
              .then(({ data }) => ({
                access: data.access_token,
                refresh: data.refresh_token,
              }))
          : await authApi
              .refreshToken(tokenState.value.refresh)
              .then(({ data }) => ({
                access: data.token,
                refresh: data.refresh,
              }));
      updateToken(response);
      return response;
    } catch (e) {
      logout();
    }
  }

  const rolesByClientId = computed<Record<string, { roles: string[] }>>(() => {
    return decodedToken.value?.resource_access ?? {};
  });

  const isSuperAdmin = computed(() => {
    return rolesByClientId.value["smx"]?.roles.includes("admin");
  });

  const userId = computed(() => {
    if (decodedToken.value === undefined) {
      void refreshToken();
      return null;
    }
    return decodedToken.value.sub;
  });

  return {
    setRealmName,
    login,
    logout,
    setPersistentStorageValue,
    updateToken,
    refreshToken,
    tokenState,
    provider,
    roles,
    decodedToken,
    rolesByClientId,
    isSuperAdmin,
    userId,
  };
});
